import React, { useRef, useEffect } from 'react';
import { itemTaxRate, orderTax, taxRateLabel } from '../../utils/tax';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { useShop } from '../../contexts/ShopContext';
import './PrintInvoice.css';

interface PrintInvoiceProps {
  orderData: any;
  onClose: () => void;
}

const PrintInvoice: React.FC<PrintInvoiceProps> = ({ orderData, onClose }) => {
  const printRef = useRef<HTMLDivElement>(null);
  const { name: shopName } = useShop();
  const brand = shopName || 'T-POS';

  // Khổ giấy máy in nhiệt: đổi thành 58 nếu dùng máy in 58mm
  const RECEIPT_WIDTH_MM = 80;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const money = (n: number) => (n || 0).toLocaleString('vi-VN') + 'đ';
    const items = getOrderItems();
    const subtotal = calculateSubtotal();
    const tax = calculateTax();
    const id = orderData?._id?.slice(-6)?.toUpperCase() || '';
    const date = new Date(orderData?.paidAt || orderData?.createdAt).toLocaleString('vi-VN');
    const staff = orderData?.staff?.username;

    const itemsHtml = items
      .map((it: any) => {
        const rate = itemTaxRate(it, orderData?.taxRate || 0);
        return `
          <div class="it">
            <div class="it-name">${it.name}${rate > 0 ? ` (thuế ${rate}%)` : ''}</div>
            <div class="row"><span>${it.quantity} x ${money(it.price)}</span><span>${money(it.price * it.quantity)}</span></div>
          </div>`;
      })
      .join('');

    const rows: string[] = [`<div class="row"><span>Tạm tính</span><span>${money(subtotal)}</span></div>`];
    if (tax > 0) rows.push(`<div class="row"><span>Thuế (${taxRateLabel({ ...orderData, items })})</span><span>${money(tax)}</span></div>`);
    if (orderData?.discount > 0) rows.push(`<div class="row"><span>Giảm giá</span><span>-${money(orderData.discount)}</span></div>`);
    rows.push(`<div class="row total"><span>TỔNG CỘNG</span><span>${money(orderData?.total)}</span></div>`);
    if (orderData?.amountPaid > 0) rows.push(`<div class="row"><span>Khách đưa</span><span>${money(orderData.amountPaid)}</span></div>`);
    if (orderData?.change > 0) rows.push(`<div class="row"><span>Thối lại</span><span>${money(orderData.change)}</span></div>`);

    const w = RECEIPT_WIDTH_MM;
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Hóa đơn ${id}</title>
          <style>
            @page { size: ${w}mm auto; margin: 0; }
            * { box-sizing: border-box; }
            body { width: ${w}mm; margin: 0; padding: 4mm 3mm; font-family: 'Courier New', monospace; font-size: 12px; line-height: 1.35; color: #000; }
            .center { text-align: center; }
            .shop { font-size: 17px; font-weight: bold; }
            .muted { font-size: 11px; }
            .hr { border-top: 1px dashed #000; margin: 6px 0; }
            .row { display: flex; justify-content: space-between; gap: 8px; margin: 2px 0; }
            .info .row { font-size: 11px; }
            .it { margin: 4px 0; }
            .it-name { font-weight: bold; }
            .total { font-weight: bold; font-size: 14px; border-top: 1px solid #000; padding-top: 4px; margin-top: 4px; }
            .foot { text-align: center; font-size: 11px; margin-top: 8px; }
          </style>
        </head>
        <body>
          <div class="center shop">${brand}</div>
          <div class="center muted">Hóa đơn thanh toán</div>
          <div class="hr"></div>
          <div class="info">
            <div class="row"><span>Mã đơn</span><span>#${id}</span></div>
            <div class="row"><span>Ngày</span><span>${date}</span></div>
            <div class="row"><span>Bàn</span><span>${getTableName()}</span></div>
            ${staff ? `<div class="row"><span>Nhân viên</span><span>${staff}</span></div>` : ''}
          </div>
          <div class="hr"></div>
          ${itemsHtml}
          <div class="hr"></div>
          ${rows.join('')}
          <div class="hr"></div>
          <div class="foot">Cảm ơn quý khách đã sử dụng dịch vụ!<br/>Hẹn gặp lại!</div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onafterprint = () => printWindow.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 300);
  };

  const handleDownloadPDF = async () => {
    if (printRef.current) {
      try {
        const loadingText = document.createElement('div');
        loadingText.textContent = 'Đang tạo PDF...';
        loadingText.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(0,0,0,0.8);
          color: white;
          padding: 20px;
          border-radius: 10px;
          z-index: 10000;
        `;
        document.body.appendChild(loadingText);

        const canvas = await html2canvas(printRef.current, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        
        const imgWidth = 210;
        const pageHeight = 295;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;

        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        const fileName = `hoa-don-${orderData?._id?.slice(-6)?.toUpperCase()}-${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(fileName);

        document.body.removeChild(loadingText);
      } catch (error) {
        console.error('Lỗi khi tạo PDF:', error);
        alert('Có lỗi khi tạo PDF. Vui lòng thử lại!');
      }
    }
  };

  const getTableName = () => {
    if (orderData?.mergedTables && orderData.mergedTables.length > 0) {
      return orderData.mergedTables.map((table: any) => table.name).join(', ');
    }
    
    if (orderData?.mergedOrders && orderData.mergedOrders.length > 0) {
      const tableNames = orderData.mergedOrders.map((order: any) => {
        if (typeof order.tableId === 'object' && order.tableId?.name) {
          return order.tableId.name;
        }
        return order.tableId || 'N/A';
      });
      return tableNames.join(', ');
    }
    
    if (orderData?.tableId) {
      if (typeof orderData.tableId === 'object' && orderData.tableId?.name) {
        return orderData.tableId.name;
      } else if (typeof orderData.tableId === 'string') {
        if (orderData.tableId.length === 24) {
          return 'N/A';
        }
        return orderData.tableId;
      }
    }
    
    return 'N/A';
  };

  const getOrderItems = () => {
    if (orderData?.isMerged && orderData?.mergedOrders) {
      const allItems: any[] = [];
      orderData.mergedOrders.forEach((order: any) => {
        if (order.items && Array.isArray(order.items)) {
          allItems.push(...order.items);
        }
      });
      return allItems;
    }
    
    return orderData?.items || [];
  };

  const calculateSubtotal = () => {
    const items = getOrderItems();
    return items.reduce((sum: number, item: any) => sum + (item.price * item.quantity), 0);
  };

  // Thuế của đơn = tổng thuế từng món; đơn cũ thì suy ra từ thuế suất của cả đơn.
  const calculateTax = () => orderTax({ ...orderData, items: getOrderItems() });

  return (
    <div className="print-invoice-overlay">
      <div className="print-invoice-modal">
        <div className="print-invoice-header">
          <h2>Hóa đơn thanh toán</h2>
          <div className="print-invoice-actions">
            <button className="print-invoice-print-btn" onClick={handlePrint}>
              🖨️ In
            </button>
            <button className="print-invoice-pdf-btn" onClick={handleDownloadPDF}>
              📄 Tải PDF
            </button>
            <button className="print-invoice-close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>
        
        <div className="print-invoice-content" ref={printRef}>
          <div className="invoice-header">
            <div className="invoice-title">
              <h1>{brand}</h1>
              <p>Hóa đơn thanh toán</p>
            </div>
            <div className="invoice-info">
              <div className="invoice-number">
                <strong>Mã đơn hàng:</strong> #{orderData?._id?.slice(-6)?.toUpperCase()}
              </div>
              <div className="invoice-date">
                <strong>Ngày:</strong> {new Date(orderData?.paidAt || orderData?.createdAt).toLocaleString('vi-VN')}
              </div>
              <div className="invoice-table">
                <strong>Bàn:</strong> {getTableName()}
              </div>
              {orderData?.staff?.username && (
                <div className="invoice-staff">
                  <strong>Nhân viên:</strong> {orderData.staff.username}
                </div>
              )}
            </div>
          </div>

          <div className="invoice-items">
            <table className="invoice-items-table">
              <thead>
                <tr>
                  <th>STT</th>
                  <th>Tên món</th>
                  <th>Đơn giá</th>
                  <th>Thuế</th>
                  <th>Số lượng</th>
                  <th>Thành tiền</th>
                </tr>
              </thead>
              <tbody>
                {getOrderItems().map((item: any, index: number) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.name}</td>
                    <td>{item.price.toLocaleString('vi-VN')}đ</td>
                    <td>{itemTaxRate(item, orderData?.taxRate || 0).toLocaleString('vi-VN')}%</td>
                    <td>{item.quantity}</td>
                    <td>{(item.price * item.quantity).toLocaleString('vi-VN')}đ</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="invoice-summary">
            <div className="invoice-summary-item">
              <span>Tạm tính:</span>
              <span>{calculateSubtotal().toLocaleString('vi-VN')}đ</span>
            </div>
            {calculateTax() > 0 && (
              <div className="invoice-summary-item">
                <span>Thuế ({taxRateLabel({ ...orderData, items: getOrderItems() })}):</span>
                <span>{Math.round(calculateTax()).toLocaleString('vi-VN')}đ</span>
              </div>
            )}
            {orderData?.discount && orderData.discount > 0 && (
              <div className="invoice-summary-item">
                <span>Giảm giá:</span>
                <span>-{orderData.discount.toLocaleString('vi-VN')}đ</span>
              </div>
            )}
            <div className="invoice-summary-total">
              <span>Tổng cộng:</span>
              <span>{orderData?.total?.toLocaleString('vi-VN')}đ</span>
            </div>
            {orderData?.amountPaid && orderData.amountPaid > 0 && (
              <div className="invoice-summary-item">
                <span>Tiền khách đưa:</span>
                <span>{orderData.amountPaid.toLocaleString('vi-VN')}đ</span>
              </div>
            )}
            {orderData?.change !== undefined && orderData.change !== null && orderData.change > 0 && (
              <div className="invoice-summary-item">
                <span>Tiền thối lại:</span>
                <span>{orderData.change.toLocaleString('vi-VN')}đ</span>
              </div>
            )}
          </div>

          <div className="invoice-footer">
            <div className="invoice-thanks">
              <p>Cảm ơn quý khách đã sử dụng dịch vụ!</p>
              <p>Hẹn gặp lại!</p>
            </div>
            <div className="invoice-signature">
              <p>Chữ ký khách hàng:</p>
              <div className="signature-line">_________________</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintInvoice; 